import React from 'react';
import { RiskReadiness } from '../../services/analyticsService';

interface Props {
  data: RiskReadiness;
}

const CX = 120;
const CY = 108;
const OUTER_R = 86;
const INNER_R = 52;
const NEEDLE_R = OUTER_R - 6;

/** Score 0–100 → degrees; 0 = left (180°), 100 = right (0°). */
function scoreToDeg(score: number): number {
  return 180 * (1 - score / 100);
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

/** Donut arc from startDeg to endDeg (upper semicircle, pivot at bottom). */
function donutArc(startDeg: number, endDeg: number): string {
  const oStart = polar(CX, CY, OUTER_R, startDeg);
  const oEnd = polar(CX, CY, OUTER_R, endDeg);
  const iStart = polar(CX, CY, INNER_R, startDeg);
  const iEnd = polar(CX, CY, INNER_R, endDeg);
  const large = Math.abs(startDeg - endDeg) > 180 ? 1 : 0;
  return [
    `M ${oStart.x} ${oStart.y}`,
    `A ${OUTER_R} ${OUTER_R} 0 ${large} 1 ${oEnd.x} ${oEnd.y}`,
    `L ${iEnd.x} ${iEnd.y}`,
    `A ${INNER_R} ${INNER_R} 0 ${large} 0 ${iStart.x} ${iStart.y}`,
    'Z',
  ].join(' ');
}

const SEGMENTS = [
  { from: 0, to: 40, color: '#ef4444' },
  { from: 40, to: 70, color: '#f59e0b' },
  { from: 70, to: 100, color: '#10b981' },
];

const RiskReadinessGauge: React.FC<Props> = ({ data }) => {
  const score = Math.min(100, Math.max(0, data?.readiness_score ?? 73));
  const needleDeg = scoreToDeg(score);
  const needleTip = polar(CX, CY, NEEDLE_R, needleDeg);
  const leftLabel = polar(CX, CY, OUTER_R + 10, 180);
  const rightLabel = polar(CX, CY, OUTER_R + 10, 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
      <h2 className="text-sm font-bold text-[#0c1e40] mb-2">Risk &amp; Continuity Readiness</h2>
      <div className="relative w-full h-[145px] my-auto">
        <svg
          viewBox="0 0 240 126"
          className="w-full h-full"
          preserveAspectRatio="xMidYMax meet"
          role="img"
          aria-label={`Risk readiness score ${Math.round(score)} out of 100`}
        >
          {SEGMENTS.map(({ from, to, color }) => (
            <path
              key={`${from}-${to}`}
              d={donutArc(scoreToDeg(from), scoreToDeg(to))}
              fill={color}
            />
          ))}
          <line
            x1={CX}
            y1={CY}
            x2={needleTip.x}
            y2={needleTip.y}
            stroke="#0c1e40"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx={CX} cy={CY} r={4.5} fill="#0c1e40" />
          <text
            x={CX}
            y={CY - OUTER_R * 0.28}
            textAnchor="middle"
            dominantBaseline="middle"
            className="font-extrabold fill-[#0c1e40]"
            style={{ fontSize: '32px', fontWeight: 800 }}
          >
            {Math.round(score)}
          </text>
          <text
            x={leftLabel.x + 4}
            y={leftLabel.y + 10}
            textAnchor="middle"
            className="fill-gray-600 text-xs font-semibold"
            style={{ fontSize: '11px' }}
          >
            0
          </text>
          <text
            x={rightLabel.x - 4}
            y={rightLabel.y + 10}
            textAnchor="middle"
            className="fill-gray-600 text-xs font-semibold"
            style={{ fontSize: '11px' }}
          >
            100
          </text>
        </svg>
      </div>
    </div>
  );
};

export default RiskReadinessGauge;
